import { Card } from 'antd';
import { useTranslation } from 'react-i18next';
import { getAccountState, type PAAccountState } from '../api/money';
import { Money } from '../money/Money';
import { FactList } from '../money/DataTable';
import { MoneyPage, QueryFrame } from '../money/MoneyPage';
import { usePAQuery } from '../money/usePAQuery';
import { periodLabel } from '../money/labels';

/**
 * «Состояние счёта» — Appendix L §6, and the smallest screen in this wave: a balance and the
 * current month's two totals.
 *
 * ⚠ BOTH TOTALS ARE THE SERVER'S SUMS AND NEITHER IS RE-ADDED HERE, and on this screen that
 * is not a formality. «Поступившие платежи» is summed over the WHOLE month INCLUDING
 * cancelled payments, because the payments ledger is append-only: a cancellation flips the
 * original's state AND posts a reversal of the opposite sign, and the two sum to zero. The
 * first version of that loop on the server skipped `state = cancelled` — which looks
 * obviously right and is wrong by the whole amount, because the original was excluded while
 * its reversal was counted, so a cancelled payment of 250 reported the month's income as
 * MINUS 250. A screen that re-summed the payment list would reintroduce exactly that choice,
 * and would get it wrong in the same direction, for the same reason.
 *
 * The period is the BILLING month, not the calendar month of the calculation timestamp, so a
 * boundary charge posted at 00:00:03 on the 1st belongs to the month it bought.
 */
export function AccountStateScreen() {
  return (
    <MoneyPage titleKey="state.title" leadKey="state.lead">
      {(contract) => <AccountStateBody contractID={contract.id} />}
    </MoneyPage>
  );
}

function AccountStateBody({ contractID }: { contractID: number }) {
  const { t } = useTranslation();
  const { query, reload } = usePAQuery<PAAccountState>(
    () => getAccountState(contractID),
    [contractID],
  );

  return (
    <QueryFrame query={query} reload={reload}>
      {(data) => (
        <Card className="pa-block">
          <FactList
            facts={[
              {
                key: 'balance',
                label: t('state.balance'),
                value: <Money minor={data.balance} currency={data.currency} strong colored />,
              },
              {
                key: 'period',
                label: t('state.period'),
                value: periodLabel(data.period, t),
              },
              {
                key: 'received',
                label: t('state.received'),
                value: <Money minor={data.payments_received} currency={data.currency} />,
                hint: t('state.receivedHint'),
              },
              {
                key: 'expenses',
                label: t('state.expenses'),
                value: <Money minor={data.expenses} currency={data.currency} />,
                hint: t('state.expensesHint'),
              },
            ]}
          />
        </Card>
      )}
    </QueryFrame>
  );
}
